
import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CalendarEvent } from "../types";
import { useShiftTemplates } from "../hooks/useShiftTemplates";
import { Loader2, Save } from "lucide-react";

interface SaveTemplateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  events: CalendarEvent[];
  onSaved: () => void;
}

const SaveTemplateDialog: React.FC<SaveTemplateDialogProps> = ({
  isOpen,
  onClose,
  teamId,
  events,
  onSaved
}) => {
  const { t } = useLanguage();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const { saveTemplate } = useShiftTemplates(teamId);

  const handleSave = async () => {
    if (!name.trim()) {
      return;
    }

    setIsSaving(true);
    try {
      const result = await saveTemplate({
        teamId,
        name: name.trim(),
        description: description.trim(),
        shifts: events,
        templateType: 'week'  // Default to week template
      });

      if (result) {
        onSaved();
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t("saveAsTemplate") || "Save as Template"}</DialogTitle>
          <DialogDescription>
            {t("saveTemplateDescription") || "Save the current schedule as a template for quick reuse"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="template-name">{t("templateName") || "Template Name"}</Label>
            <Input
              id="template-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("enterTemplateName") || "Enter template name"}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="template-description">{t("description") || "Description"} ({t("optional") || "optional"})</Label>
            <Textarea
              id="template-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("enterTemplateDescription") || "Enter template description"}
              rows={3}
            />
          </div>

          <div>
            <p className="text-sm text-muted-foreground">
              {t("shiftsToSave") || "Shifts to save"}: <span className="font-medium">{events.length}</span>
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            {t("cancel") || "Cancel"}
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={!name.trim() || isSaving}
            className="gap-2"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {t("saveTemplate") || "Save Template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SaveTemplateDialog;
